import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Coins, LogOut, Plus, Snowflake } from "lucide-react";
import { HeaderNav } from "../components/HeaderNav";
import { authApi } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useLang } from "../lib/i18n";
import { fmtEur } from "./Payments";

const packName = (id, t) => `${(id || "").replace("pack_", "")} ${t("credit_word_many")}`;

export const actionLabel = (row, t) => {
  if (row.action === "signup") return t("ledger_signup");
  if (row.action === "purchase") return `${t("ledger_purchase")} · ${packName(row.label, t)}`;
  if (row.action === "refund") return `${t("ledger_refund")}${row.label ? ` · ${row.label}` : ""}`;
  return `${t(`ledger_${row.action}`)}${row.label ? ` · ${row.label}` : ""}`;
};

const PAY_STATUS = { completed: "paid", initiated: "pending", cancelled: "cancelled", expired: "expired", amount_mismatch: "failed" };

const Purchases = ({ rows }) => {
  const { t, lang } = useLang();
  if (!rows?.length) return <p className="frz-muted" data-testid="purchases-empty">{t("purchases_empty")}</p>;
  return (
    <div className="frz-table-wrap">
      <table className="frz-table" data-testid="purchases-table">
        <thead><tr><th>{t("history_date")}</th><th>{t("purchase_pack")}</th><th>{t("history_amount")}</th><th>{t("credit_word_many")}</th><th>{t("purchase_status")}</th></tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.tx_id} data-testid={`purchase-row-${i}`}>
              <td>{new Date(r.created_at).toLocaleString(lang)}</td>
              <td>{packName(r.pack_id, t)}</td>
              <td>{fmtEur(r.amount, lang)}</td>
              <td className={r.credited ? "pos" : ""}>{r.credited ? `+${r.credits}` : "—"}</td>
              <td><span className={`frz-badge is-${r.credited ? "done" : r.status === "initiated" ? "running" : "error"}`} data-testid={`purchase-status-${i}`}>{t(`pay_st_${PAY_STATUS[r.status] || "pending"}`)}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default function Account() {
  const { t, lang } = useLang();
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [purchases, setPurchases] = useState(null);
  useEffect(() => {
    refresh();
    authApi.history().then(setRows).catch(() => setRows([]));
    authApi.purchases().then(setPurchases).catch(() => setPurchases([]));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="frz-page">
      <HeaderNav />
      <main className="frz-section" data-testid="account-page">
        <h1 className="frz-h1">{t("account_title")}</h1>
        <div className="frz-account-grid">
          <section className="frz-step">
            <h3>{user.name}</h3>
            <p className="frz-muted" data-testid="account-email">{user.email}</p>
            <p className="frz-small frz-muted">{t("account_providers")} : {user.providers.join(", ")}</p>
            <button type="button" className="frz-btn frz-btn-ghost frz-btn-sm" onClick={() => { nav("/", { replace: true }); logout(); }} data-testid="account-logout-button">
              <LogOut size={14} /> {t("logout")}
            </button>
          </section>
          <section className="frz-step frz-balance-card">
            <span className="frz-label">{t("balance")}</span>
            <p className="frz-balance" data-testid="account-balance"><Snowflake size={26} /> {user.credits}</p>
            <Link to="/recharge" className="frz-btn frz-btn-primary" data-testid="account-recharge-button"><Plus size={16} /> {t("recharge")}</Link>
          </section>
        </div>
        <h2 className="frz-h2">{t("purchases_title")}</h2>
        <Purchases rows={purchases} />
        <h2 className="frz-h2">{t("history_title")}</h2>
        <div className="frz-table-wrap">
          <table className="frz-table" data-testid="credit-history-table">
            <thead><tr><th>{t("history_date")}</th><th>{t("history_action")}</th><th>{t("history_amount")}</th><th>{t("balance")}</th></tr></thead>
            <tbody>
              {rows?.map((r, i) => (
                <tr key={`${r.created_at}-${i}`} data-testid={`history-row-${i}`}>
                  <td>{new Date(r.created_at).toLocaleString(lang)}</td>
                  <td>{actionLabel(r, t)}</td>
                  <td className={r.amount >= 0 ? "pos" : "neg"}>{r.amount > 0 ? `+${r.amount}` : r.amount}</td>
                  <td><Coins size={12} /> {r.balance_after}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
